import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { AdminGate } from "@/components/admin/AdminGate";
import { getPlacarMes, savePlacarMeta, type PlacarMes, type Corte } from "@/lib/placar/placar.functions";
import { FATIAS, GRUPOS_LEAD, diaBR, mesAnterior, ritmoDoMes } from "@/lib/placar/placar-calc";
import { BlackManychat } from "@/components/black2x1/BlackManychat";
import { BlackVendasExtras } from "@/components/black2x1/BlackVendasExtras";
import { BlackTorneiras } from "@/components/black2x1/BlackTorneiras";
import { Recolhivel } from "@/components/black2x1/Recolhivel";

export const Route = createFileRoute("/admin/placar")({
  head: () => ({
    meta: [
      { title: "Admin · Placar do Jogo" },
      { name: "description", content: "Gasto, leads e vendas novas de toda a operação, mês a mês." },
      { property: "og:title", content: "Admin · Placar do Jogo" },
      { property: "og:description", content: "Gasto, leads e vendas novas de toda a operação, mês a mês." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/placar"><PlacarPage /></AdminGate>,
});

const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const brlC = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const num = (v: number) => v.toLocaleString("pt-BR");
const pct = (v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const div = (a: number, b: number) => (b > 0 ? a / b : null);
const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const nomeMes = (m: string) => `${MESES[Number(m.slice(5, 7)) - 1]} de ${m.slice(0, 4)}`;

function Kpi({ label, value, linhas }: { label: string; value: string; linhas?: [string, string][] }) {
  return (
    <div className="flex flex-col rounded-xl border border-black-campaign-purple/40 bg-black-campaign-surface p-6">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 whitespace-nowrap text-2xl font-bold tracking-tight text-foreground">{value}</p>
      {linhas && (
        <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
          {linhas.map(([k, v]) => <div key={k} className="flex justify-between gap-2"><span>{k}</span><span className="font-semibold text-foreground">{v}</span></div>)}
        </div>
      )}
    </div>
  );
}

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-black-campaign-purple/40 bg-card p-6 sm:p-8">
      <h2 className="mb-6 text-lg font-bold uppercase tracking-[0.15em] text-black-campaign-lilac">{titulo}</h2>
      {children}
    </section>
  );
}

function PlacarPage() {
  const [mes, setMesRaw] = useState(() => diaBR(new Date().toISOString()).slice(0, 7));
  const [periodo, setPeriodo] = useState<{ de: string; ate: string } | null>(null);
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const setMes = (m: string) => { setMesRaw(m); setPeriodo(null); setDe(""); setAte(""); };
  const fn = useServerFn(getPlacarMes);
  const q = useQuery({
    queryKey: ["placar", mes, periodo?.de ?? "", periodo?.ate ?? ""],
    queryFn: () => fn({ data: { mes, ...(periodo?.de ? { de: periodo.de } : {}), ...(periodo?.ate ? { ate: periodo.ate } : {}) } }),
    staleTime: 5 * 60_000,
  });
  const atual = diaBR(new Date().toISOString()).slice(0, 7);
  const meses = Array.from({ length: 24 }, (_, i) => mesAnterior(atual, i));
  const fimMes = `${mes}-${String(new Date(Date.UTC(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)), 0)).getUTCDate()).padStart(2, "0")}`;
  const inp = "rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground";

  return (
    <div className="placar-scope min-h-screen"><div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3 w-3" /> Admin</Link>
          <h1 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl"><span className="text-black-campaign-lilac">PLACAR DO JOGO</span> · OPERAÇÃO FEO</h1>
          <p className="text-sm text-muted-foreground">Toda a operação no mês: gasto, leads e vendas novas.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setMes(mesAnterior(mes))} className="rounded-md border border-border p-2 hover:bg-muted" aria-label="Mês anterior"><ChevronLeft className="h-4 w-4" /></button>
          <select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Escolher mês" className="min-w-44 rounded-md border border-border bg-background px-3 py-2 text-center font-semibold capitalize text-foreground">
            {meses.map((m) => <option key={m} value={m}>{nomeMes(m)}</option>)}
          </select>
          <button type="button" disabled={mes >= atual} onClick={() => setMes(mesAnterior(mes, -1))} className="rounded-md border border-border p-2 hover:bg-muted disabled:opacity-40" aria-label="Próximo mês"><ChevronRight className="h-4 w-4" /></button>
          <button type="button" onClick={() => q.refetch()} className="rounded-md border border-border p-2 hover:bg-muted" aria-label="Atualizar"><RefreshCw className={`h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-black-campaign-purple/40 bg-card/60 px-4 py-3 text-sm">
        <span className="font-semibold text-foreground">Período:</span>
        <button type="button" onClick={() => { setPeriodo(null); setDe(""); setAte(""); }} className={`rounded-md border px-3 py-1.5 font-semibold ${!periodo ? "border-black-campaign-lilac text-black-campaign-lilac" : "border-border text-muted-foreground"}`}>Total do mês</button>
        <label className="flex items-center gap-2 text-muted-foreground">De <input type="date" value={de} min={`${mes}-01`} max={fimMes} onChange={(e) => setDe(e.target.value)} className={inp} /></label>
        <label className="flex items-center gap-2 text-muted-foreground">Até <input type="date" value={ate} min={`${mes}-01`} max={fimMes} onChange={(e) => setAte(e.target.value)} className={inp} /></label>
        <button type="button" disabled={!de && !ate} onClick={() => setPeriodo({ de, ate: ate && de && ate < de ? de : ate })} className="rounded-md border border-border px-3 py-1.5 font-semibold text-foreground disabled:opacity-40">Aplicar</button>
        {periodo && <span className="text-xs text-black-campaign-lilac">Mostrando {periodo.de ? periodo.de.split("-").reverse().slice(0, 2).join("/") : "início do mês"} a {periodo.ate ? periodo.ate.split("-").reverse().slice(0, 2).join("/") : "fim do mês"}</span>}
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Carregando o placar…</p>}
      {q.error && <p className="text-sm text-destructive">Não foi possível carregar: {(q.error as Error).message}</p>}
      {q.data && <Conteudo d={q.data} />}
    </div></div>
  );
}

function MetaEditor({ d }: { d: PlacarMes }) {
  const save = useServerFn(savePlacarMeta);
  const qc = useQueryClient();
  const [fat, setFat] = useState(d.meta.faturamento != null ? String(d.meta.faturamento) : "");
  const [vp, setVp] = useState(d.meta.vendasPos != null ? String(d.meta.vendasPos) : "");
  const [st, setSt] = useState<string | null>(null);
  const n = (v: string) => { const x = Number(v.replace(/\./g, "").replace(",", ".")); return v.trim() && Number.isFinite(x) ? x : null; };
  const gravar = async () => {
    setSt("Salvando…");
    try {
      await save({ data: { mes: d.mes, faturamento: n(fat), vendasPos: n(vp) != null ? Math.round(n(vp)!) : null } });
      await qc.invalidateQueries({ queryKey: ["placar", d.mes] });
      setSt("Meta salva");
    } catch (e) { setSt(e instanceof Error ? e.message : "Erro ao salvar"); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="text-xs text-muted-foreground">Meta de faturamento do mês (R$)
        <input inputMode="decimal" value={fat} onChange={(e) => setFat(e.target.value)} placeholder="0" className="mt-1 block w-48 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground" />
      </label>
      <label className="text-xs text-muted-foreground">Meta de vendas de pós
        <input inputMode="numeric" value={vp} onChange={(e) => setVp(e.target.value)} placeholder="0" className="mt-1 block w-36 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground" />
      </label>
      <button type="button" onClick={gravar} className="rounded-md bg-black-campaign-purple px-4 py-2 text-sm font-bold text-primary-foreground">Salvar metas</button>
      {st && <span className="text-xs text-muted-foreground">{st}</span>}
    </div>
  );
}

function Ritmo({ titulo, meta, real, fmt, totalDias, passados }: { titulo: string; meta: number | null; real: number; fmt: (v: number) => string; totalDias: number; passados: number }) {
  if (!meta) return <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">{titulo}: cadastre a meta acima.</div>;
  const r = ritmoDoMes(meta, real, totalDias, passados);
  const bom = r.pctRitmo >= 100;
  return (
    <div className="rounded-xl border border-black-campaign-purple/40 bg-black-campaign-surface p-6">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{titulo}</p>
      <p className="mt-1 text-2xl font-extrabold text-foreground">{fmt(real)} <span className="text-sm font-semibold text-muted-foreground">de {fmt(meta)} · {pct(r.pctMeta)}</span></p>
      <div className="mt-2 h-2 rounded bg-muted"><div className="h-2 rounded bg-black-campaign-purple" style={{ width: `${Math.min(100, r.pctMeta)}%` }} /></div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <span className="text-muted-foreground">Esperado até hoje</span><span className="text-right text-foreground">{fmt(r.esperado)}</span>
        <span className="text-muted-foreground">Ritmo</span><span className={`text-right font-semibold ${bom ? "text-black-campaign-cyan" : "text-amber-500"}`}>{pct(r.pctRitmo)} {bom ? "no ritmo" : "abaixo do ritmo"}</span>
        <span className="text-muted-foreground">Falta por dia ({r.restantes} dias)</span><span className="text-right text-foreground">{fmt(r.faltaPorDia)}</span>
        <span className="text-muted-foreground">Projeção do mês</span><span className="text-right text-foreground">{fmt(r.projecao)}</span>
      </div>
    </div>
  );
}

function TabelaCorte({ linhas }: { linhas: Corte[] }) {
  return (
    <table className="w-full text-xs">
      <thead className="text-[10px] uppercase tracking-wide text-muted-foreground"><tr className="text-left"><th className="py-3">Item</th><th className="text-right">Leads</th><th className="text-right">Vendas</th><th className="text-right">Faturamento</th><th className="text-right">Conv.</th></tr></thead>
      <tbody>{linhas.slice(0, 40).map((c) => (
        <tr key={c.chave} className="border-t border-border/40 text-foreground hover:bg-black-campaign-purple/5"><td className="py-2.5 pr-2">{c.chave}</td><td className="text-right">{num(c.leads)}</td><td className="text-right">{num(c.vendas)}</td><td className="text-right">{brl(c.valor)}</td><td className="text-right text-muted-foreground">{c.leads ? pct((c.vendas / c.leads) * 100) : "·"}</td></tr>
      ))}</tbody>
    </table>
  );
}

const OBJ_LABEL = Object.fromEntries(FATIAS.map((f) => [f.key, f.label])) as Record<string, string>;
const fmtD = (d: string) => `${d.slice(8)}/${d.slice(5, 7)}`;

const ativa = (st: string | null) => /ACTIVE|ENABLED/.test(st ?? "");

function CampanhasPorPlataforma({ camps }: { camps: PlacarMes["campanhas"] }) {
  const total = camps.reduce((s, c) => s + c.gasto, 0);
  const plats = [
    { key: "meta", label: "Meta Ads", cor: "text-placar-meta", borda: "border-placar-meta/40", barra: "bg-placar-meta" },
    { key: "google", label: "Google Ads", cor: "text-placar-google", borda: "border-placar-google/40", barra: "bg-placar-google" },
  ];
  return (
    <Recolhivel title="Campanhas contabilizadas" resumo={`${camps.length} campanhas · ${brlC(total)}`}>
      <div className="space-y-6">
        {plats.map((p) => {
          const lista = camps.filter((c) => c.plataforma === p.key);
          const tp = lista.reduce((s, c) => s + c.gasto, 0);
          const grupos = new Map<string, typeof lista>();
          for (const c of lista) grupos.set(c.grupo, [...(grupos.get(c.grupo) ?? []), c]);
          const ord = [...grupos.entries()].map(([g, cs]) => ({ g, cs, gasto: cs.reduce((s, c) => s + c.gasto, 0) })).sort((x, y) => y.gasto - x.gasto);
          return (
            <div key={p.key} className={`rounded-xl border ${p.borda} p-5`}>
              <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className={`text-base font-bold uppercase tracking-[0.15em] ${p.cor}`}>{p.label}</h3>
                <span className="text-sm text-foreground">{lista.length} campanhas · <span className={`font-bold ${p.cor}`}>{brlC(tp)}</span> <span className="text-muted-foreground">· {total ? pct((tp / total) * 100) : "·"} do total</span></span>
              </div>
              {!lista.length && <p className="text-xs text-muted-foreground">Sem gasto no período.</p>}
              <div className="space-y-5">
                {ord.map(({ g, cs, gasto }) => (
                  <div key={g}>
                    <div className="flex justify-between gap-3 text-sm"><span className="font-semibold text-foreground">{g} <span className="text-xs text-muted-foreground">({cs.length})</span></span><span className="text-foreground">{brlC(gasto)} <span className="text-muted-foreground">· {tp ? pct((gasto / tp) * 100) : "·"}</span></span></div>
                    <div className="mt-1 h-1.5 rounded-full bg-muted"><div className={`h-1.5 rounded-full ${p.barra}`} style={{ width: `${tp ? (gasto / tp) * 100 : 0}%` }} /></div>
                    <table className="mt-2 w-full text-xs">
                      <tbody>{cs.map((c) => (
                        <tr key={c.conta + c.campanha} className="border-t border-border/40 text-foreground">
                          <td className="py-2 pr-2">{c.campanha}<div className="text-[10px] text-muted-foreground">{c.conta} · {OBJ_LABEL[c.objetivo]}</div></td>
                          <td className={`w-20 pr-2 ${ativa(c.status) ? p.cor : "text-muted-foreground"}`}>{ativa(c.status) ? "Ativa" : c.status ? "Pausada" : "·"}</td>
                          <td className="w-28 text-right">{brlC(c.gasto)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Recolhivel>
  );
}

function Conteudo({ d }: { d: PlacarMes }) {
  const [aberta, setAberta] = useState<string | null>(null);
  const [corte, setCorte] = useState<keyof PlacarMes["cortes"]>("vendedora");
  const V = d.vendas;
  const vendas = V.pos.qtd + V.black.qtd + V.outros.qtd;
  const fat = V.pos.valor + V.black.valor + V.outros.valor;
  const unicas = V.pos.unicas + V.black.unicas + V.outros.unicas;
  const ass = V.pos.assinaturas + V.black.assinaturas + V.outros.assinaturas;
  const vU = V.pos.valorUnicas + V.black.valorUnicas + V.outros.valorUnicas;
  const vA = V.pos.valorAssinaturas + V.black.valorAssinaturas + V.outros.valorAssinaturas;
  const inv = d.investido.total;
  const cpl = div(inv, d.leads.total);
  const tiposLead = Object.entries(d.leads.porTipo).sort((a, b) => b[1] - a[1]);
  const canais = Object.entries(d.leads.porCanal).sort((a, b) => b[1] - a[1]).slice(0, 12);
  const a = d.anoAnterior;
  const varia = (x: number, y: number | null) => (y && y > 0 ? pct(((x - y) / y) * 100) : "·");
  const roas = div(fat, inv);
  const roasAnt = a.faturamento != null && a.investido ? a.faturamento / a.investido : null;
  const hoje = diaBR(new Date().toISOString());
  const totalDias = d.porDia.length;
  const passados = d.porDia.filter((x) => x.dia <= hoje).length;
  const diasDesc = [...d.porDia].filter((x) => x.dia <= hoje).reverse();
  const tk = (v: number, q: number) => (q ? brlC(v / q) : "·");

  return (
    <>
      {(!d.fontes.meta || !d.fontes.google) && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground">Parte do gasto não carregou ({!d.fontes.meta ? "Meta Ads" : ""}{!d.fontes.meta && !d.fontes.google ? " e " : ""}{!d.fontes.google ? "Google Ads" : ""}). Os números de investimento estão parciais.</p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Investido total" value={brlC(inv)} linhas={[["Meta Ads", brlC(d.investido.meta)], ["Google Ads", brlC(d.investido.google)], ["ManyChat", brlC(d.investido.manychat)]]} />
        <Kpi label="Leads do mês" value={num(d.leads.total)} linhas={tiposLead.slice(0, 6).map(([k, v]) => [k, num(v)])} />
        <Kpi label="Custo por lead" value={cpl != null ? brlC(cpl) : "·"} linhas={[["Gasto em captação + funis", brlC((d.fatias.find((f) => f.key === "captacao")?.gasto ?? 0) + (d.fatias.find((f) => f.key === "funis")?.gasto ?? 0))], ["Leads pagos e orgânicos", num(d.leads.total)]]} />
        <Kpi label="Vendas novas" value={num(vendas)} linhas={[["Pós-graduação", num(V.pos.qtd)], ["Black 2x1", num(V.black.qtd)], ["Outros produtos", num(V.outros.qtd)]]} />
        <Kpi label="Faturamento novo" value={brl(fat)} linhas={[["Pós-graduação", brl(V.pos.valor)], ["Black 2x1", brl(V.black.valor)], ["Outros produtos", brl(V.outros.valor)]]} />
        <Kpi label="Ticket médio" value={tk(fat, vendas)} linhas={[["Única", tk(vU, unicas)], ["Assinatura", tk(vA, ass)], ["Pós", tk(V.pos.valor, V.pos.qtd)]]} />
        <Kpi label="ROAS / custo por venda" value={roas != null ? `${roas.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}x` : "·"} linhas={[["Custo por venda", vendas ? brlC(inv / vendas) : "·"], ["Única / assinatura", `${num(unicas)} / ${num(ass)}`]]} />
        <Kpi label="Meta do mês" value={d.meta.faturamento ? brl(d.meta.faturamento) : "Sem meta"} linhas={[["Faturamento atingido", d.meta.faturamento ? pct((fat / d.meta.faturamento) * 100) : "·"], ["Vendas de pós", d.meta.vendasPos ? `${num(V.pos.qtd)} de ${num(d.meta.vendasPos)}` : "·"]]} />
      </div>

      <Bloco titulo="Ritmo do mês">
        <MetaEditor key={d.mes} d={d} />
        <p className="mt-2 text-xs text-muted-foreground">Dia {passados} de {totalDias}. Ao salvar, o ritmo, as % e a projeção se atualizam.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Ritmo titulo="Faturamento" meta={d.meta.faturamento} real={fat} fmt={brl} totalDias={totalDias} passados={passados} />
          <Ritmo titulo="Vendas de pós" meta={d.meta.vendasPos} real={V.pos.qtd} fmt={(v) => num(Math.round(v * 10) / 10)} totalDias={totalDias} passados={passados} />
        </div>
      </Bloco>

      <Bloco titulo="Divisão do gasto">
        <div className="space-y-2">
          {d.fatias.map((f) => (
            <Fragment key={f.key}>
              <button type="button" onClick={() => setAberta(aberta === f.key ? null : f.key)} className="w-full text-left">
                <div className="flex justify-between text-sm"><span className="font-semibold text-foreground">{f.label} {f.campanhas.length ? <span className="text-xs text-muted-foreground">({f.campanhas.length})</span> : null}</span><span className="text-foreground">{brlC(f.gasto)} <span className="text-muted-foreground">· {inv ? pct((f.gasto / inv) * 100) : "·"}</span></span></div>
                <div className="mt-1 h-2 rounded bg-muted"><div className="h-2 rounded bg-black-campaign-purple" style={{ width: `${inv ? (f.gasto / inv) * 100 : 0}%` }} /></div>
              </button>
              {aberta === f.key && f.campanhas.length > 0 && (
                <div className="ml-3 space-y-1 border-l border-border pl-3 text-xs">
                  {f.campanhas.map((c) => <div key={c.plataforma + c.campanha} className="flex justify-between gap-3"><span className="text-muted-foreground">{c.plataforma} · {c.campanha}</span><span className="text-foreground">{brlC(c.gasto)}</span></div>)}
                </div>
              )}
            </Fragment>
          ))}
          <p className="pt-2 text-xs text-muted-foreground">A divisão segue o nome das campanhas (grupos de gasto editáveis no painel da Black → Investimento).</p>
        </div>
      </Bloco>

      <Bloco titulo="Dia a dia">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground"><tr className="text-left"><th className="py-3 pr-2">Dia</th><th className="text-right">Gasto total</th>{FATIAS.map((f) => <th key={f.key} className="text-right">{f.label}</th>)}<th className="text-right">Leads</th><th className="text-right">CPL</th><th className="text-right">Pós</th><th className="text-right">Black</th><th className="text-right">Outros</th><th className="text-right">Faturamento</th><th className="text-right">ROAS</th></tr></thead>
            <tbody>
              {diasDesc.map((x) => {
                const c = div(x.gasto, x.leads); const ro = div(x.faturamento, x.gasto);
                return (
                  <tr key={x.dia} className="border-t border-border/40 text-foreground hover:bg-black-campaign-purple/5">
                    <td className="py-2.5 pr-2">{fmtD(x.dia)}</td><td className="text-right font-semibold">{brlC(x.gasto)}</td>
                    {FATIAS.map((f) => <td key={f.key} className="text-right text-muted-foreground">{x.gastoFatia[f.key] ? brlC(x.gastoFatia[f.key]) : "·"}</td>)}
                    <td className="text-right">{num(x.leads)}</td><td className="text-right">{c != null ? brlC(c) : "·"}</td>
                    <td className="text-right">{num(x.vendasPos)}</td><td className="text-right">{num(x.vendasBlack)}</td><td className="text-right">{num(x.vendasOutros)}</td>
                    <td className="text-right">{brl(x.faturamento)}</td><td className="text-right">{ro != null ? `${ro.toFixed(2).replace(".", ",")}x` : "·"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Bloco>


      <div className="flex items-center gap-4 pt-10">
        <h2 className="text-lg font-bold uppercase tracking-[0.15em] text-black-campaign-lilac">Vendas</h2>
        <span className="h-px flex-1 bg-gradient-to-r from-black-campaign-purple/60 to-transparent" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Bloco titulo="Vendas por dia">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.porDia.map((x) => ({ d: x.dia.slice(8), pos: x.vendasPos, black: x.vendasBlack, outros: x.vendasOutros, fat: x.faturamento }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="d" fontSize={11} stroke="var(--muted-foreground)" />
                <YAxis yAxisId="l" fontSize={11} stroke="var(--muted-foreground)" />
                <YAxis yAxisId="r" orientation="right" fontSize={11} stroke="var(--muted-foreground)" />
                <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="l" stackId="v" dataKey="pos" name="Vendas pós" fill="var(--chart-1)" />
                <Bar yAxisId="l" stackId="v" dataKey="black" name="Vendas Black 2x1" fill="var(--chart-2)" />
                <Bar yAxisId="l" stackId="v" dataKey="outros" name="Vendas outros" fill="var(--chart-4)" />
                <Line yAxisId="r" dataKey="fat" name="Faturamento (R$)" stroke="var(--chart-3)" dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Bloco>
        <div className="space-y-6">
          <Bloco titulo="Comparativo com o ano anterior">
            {a.fonte === "sem-dado" ? <p className="text-sm text-muted-foreground">Sem dado do mesmo mês do ano passado.</p> : (
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground"><tr className="text-left"><th /><th>Este mês</th><th>Ano anterior</th><th>Variação</th></tr></thead>
                <tbody className="text-foreground">
                  <tr className="border-t border-border"><td className="py-2.5">Investido</td><td>{brl(inv)}</td><td>{a.investido != null ? brl(a.investido) : "sem dado"}</td><td>{varia(inv, a.investido)}</td></tr>
                  <tr className="border-t border-border"><td className="py-2.5">Leads</td><td>{num(d.leads.total)}</td><td>{a.leads != null ? num(a.leads) : "sem dado"}</td><td>{varia(d.leads.total, a.leads)}</td></tr>
                  <tr className="border-t border-border"><td className="py-2.5">Vendas</td><td>{num(vendas)}</td><td>{a.vendas != null ? num(a.vendas) : "sem dado"}</td><td>{varia(vendas, a.vendas)}</td></tr>
                  <tr className="border-t border-border"><td className="py-2.5">Faturamento</td><td>{brl(fat)}</td><td>{a.faturamento != null ? brl(a.faturamento) : "sem dado"}</td><td>{varia(fat, a.faturamento)}</td></tr>
                  <tr className="border-t border-border"><td className="py-2.5">ROAS</td><td>{roas != null ? roas.toFixed(2).replace(".", ",") : "·"}</td><td>{roasAnt != null ? roasAnt.toFixed(2).replace(".", ",") : "sem dado"}</td><td>{roas != null ? varia(roas, roasAnt) : "·"}</td></tr>
                </tbody>
              </table>
            )}
            {a.fonte === "placar" && <p className="mt-2 text-xs text-muted-foreground">Ano anterior vem do placar importado (totais, sem separar vendas novas).</p>}
          </Bloco>
        </div>
      </div>
      <div>
        <Bloco titulo="Vendas por produto">
          {(["pos", "black", "outros"] as const).map((linha) => (
            <div key={linha} className="mb-4">
              <p className="mb-1 text-xs font-bold uppercase text-muted-foreground">{linha === "pos" ? "Pós-graduação" : linha === "black" ? "Black 2x1" : "Outros produtos"}</p>
              {V.porProduto.filter((p) => p.linha === linha).map((p) => (
                <div key={p.produto} className="flex justify-between gap-3 border-t border-border py-1 text-xs"><span className="text-foreground">{p.produto}</span><span className="whitespace-nowrap text-muted-foreground">{num(p.qtd)} ({num(p.unicas)} únicas / {num(p.assinaturas)} ass.) · <span className="text-foreground">{brl(p.valor)}</span></span></div>
              ))}
              {!V.porProduto.some((p) => p.linha === linha) && <p className="text-xs text-muted-foreground">Sem vendas novas.</p>}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">Só vendas novas: sem renovações, sem produtos gratuitos e sem testes. Assinatura pelo valor total do contrato; venda única sem os juros do parcelamento (Outros da Eduzz).</p>
        </Bloco>
      </div>
      <div className="flex items-center gap-4 pt-10">
        <h2 className="text-lg font-bold uppercase tracking-[0.15em] text-black-campaign-lilac">Leads</h2>
        <span className="h-px flex-1 bg-gradient-to-r from-black-campaign-purple/60 to-transparent" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Bloco titulo="Leads por dia">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.porDia.map((x) => ({ d: x.dia.slice(8), ...x.leadsGrupo }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="d" fontSize={11} stroke="var(--muted-foreground)" />
                <YAxis fontSize={11} stroke="var(--muted-foreground)" />
                <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {GRUPOS_LEAD.map((g, i) => <Bar key={g.key} stackId="l" dataKey={g.key} name={g.label} fill={`var(--chart-${i + 1})`} />)}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Bloco>
          <Bloco titulo="Onde investir">
            <p className="mb-2 text-xs text-muted-foreground">Leads por tipo de captura e por canal (UTM) no mês.</p>
            <div className="grid gap-4 text-xs sm:grid-cols-2">
              <div>{tiposLead.map(([k, v]) => <div key={k} className="flex justify-between border-t border-border py-1"><span className="text-foreground">{k}</span><span className="text-muted-foreground">{num(v)} · {pct((v / Math.max(1, d.leads.total)) * 100)}</span></div>)}</div>
              <div>{canais.map(([k, v]) => <div key={k} className="flex justify-between border-t border-border py-1"><span className="text-foreground">{k}</span><span className="text-muted-foreground">{num(v)}</span></div>)}</div>
            </div>
          </Bloco>
      </div>
      <Bloco titulo="Leads e vendas por corte">
        <div className="mb-3 flex flex-wrap gap-2">
          {([["vendedora", "Vendedora"], ["canal", "Canal"], ["origem", "Origem"], ["fonte", "Fonte (UTMs)"]] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setCorte(k)} className={`rounded-md border px-3 py-1 text-xs ${corte === k ? "border-black-campaign-lilac text-black-campaign-lilac" : "border-border text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
        <TabelaCorte linhas={d.cortes[corte]} />
        <p className="mt-2 text-xs text-muted-foreground">Venda ligada ao lead pelo e-mail (ou telefone, nas vendas externas). Vendedora só existe nos leads da Black e nas vendas pelo link do CRM. Fonte = utm_source / utm_medium / utm_campaign.</p>
      </Bloco>

      <div className="flex items-center gap-4 pt-10">
        <h2 className="text-lg font-bold uppercase tracking-[0.15em] text-black-campaign-lilac">Configurações</h2>
        <span className="h-px flex-1 bg-gradient-to-r from-black-campaign-purple/60 to-transparent" />
      </div>
      <Recolhivel title="Fluxo de captura e envio da operação" resumo={`${num(d.crm.captados)} captados · ${num(d.crm.fila)} na fila · ${num(d.crm.erro)} com erro`}>
        <div className="grid gap-3 sm:grid-cols-5">
          {([["Captados", d.crm.captados], ["Enviados RD Station", d.crm.rd], ["Enviados ao CRM", d.crm.crm], ["Na fila", d.crm.fila], ["Com erro", d.crm.erro]] as const).map(([k, v]) => (
            <div key={k} className="rounded-xl border border-black-campaign-purple/40 bg-black-campaign-surface p-4"><p className="text-[11px] uppercase text-muted-foreground">{k}</p><p className="text-xl font-extrabold text-foreground">{num(v)}</p><p className="text-[11px] text-muted-foreground">{d.crm.captados ? pct((v / d.crm.captados) * 100) : "·"}</p></div>
          ))}
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground"><tr className="text-left"><th className="py-3">Dia</th><th className="text-right">Captados</th><th className="text-right">RD Station</th><th className="text-right">CRM</th><th className="text-right">Fila</th><th className="text-right">Erro</th></tr></thead>
            <tbody>{d.crm.porDia.map((x) => <tr key={x.dia} className="border-t border-border/40 text-foreground hover:bg-black-campaign-purple/5"><td className="py-2.5">{fmtD(x.dia)}</td><td className="text-right">{num(x.captados)}</td><td className="text-right">{num(x.rd)}</td><td className="text-right">{num(x.crm)}</td><td className="text-right">{num(x.fila)}</td><td className={`text-right ${x.erro ? "text-destructive" : ""}`}>{num(x.erro)}</td></tr>)}</tbody>
          </table>
        </div>
              </Recolhivel>
      <CampanhasPorPlataforma camps={d.campanhas} />
      <BlackTorneiras />
      <BlackManychat />
      <BlackVendasExtras />
    </>
  );
}
